import { RuntimeInstance } from './RuntimeInstance'

const INDENT_UNIT = '  '
const MAX_DEPTH = 8

const UNREADABLE = Symbol('unreadable')

type Seen = WeakSet<object>

function indent(level: number): string {
  return INDENT_UNIT.repeat(level)
}

function isPlainPrimitive(value: unknown): boolean {
  return value === null || (typeof value !== 'object' && typeof value !== 'function')
}

function getTypeName(value: object): string | undefined {
  try {
    const ctor = (value as { constructor?: { name?: string } }).constructor
    const name = ctor?.name
    if (!name || name === 'Object') return undefined
    return name
  } catch {
    return undefined
  }
}

function getReadableEntries(value: object): Array<[string, unknown]> {
  let keys: string[]
  try {
    keys = Object.keys(value as Record<string, unknown>)
  } catch {
    return []
  }

  const entries: Array<[string, unknown]> = []
  for (const key of keys) {
    let propValue: unknown
    try {
      propValue = (value as Record<string, unknown>)[key]
    } catch {
      entries.push([key, UNREADABLE])
      continue
    }
    if (typeof propValue === 'function') continue
    entries.push([key, propValue])
  }
  return entries
}

function getCustomToString(value: object): string | undefined {
  try {
    const ts = (value as { toString?: () => unknown }).toString
    if (typeof ts !== 'function' || ts === Object.prototype.toString) return undefined
    const out = ts.call(value)
    if (typeof out !== 'string') return undefined
    if (out.startsWith('[object ') && out.endsWith(']')) return undefined
    return out
  } catch {
    return undefined
  }
}

function formatPrimitiveQuoted(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'string') return JSON.stringify(value)
  if (typeof value === 'bigint') return `${value.toString()}n`
  if (typeof value === 'symbol') return value.toString()
  return String(value)
}

function formatArray(value: unknown[], seen: Seen, level: number): string {
  if (value.length === 0) return '[]'

  const allPrimitive = value.every(isPlainPrimitive)
  if (allPrimitive) {
    return `[${value.map(formatPrimitiveQuoted).join(', ')}]`
  }

  const inner = value.map((item) => `${indent(level + 1)}${formatStructured(item, seen, level + 1)}`).join(',\n')
  return `[\n${inner}\n${indent(level)}]`
}

function formatObject(value: object, seen: Seen, level: number): string {
  const typeName = getTypeName(value)
  const entries = getReadableEntries(value)

  if (entries.length === 0) {
    const customStr = getCustomToString(value)
    if (typeName && customStr) return `${typeName}: ${customStr}`
    if (typeName) return `Instance of ${typeName}`
    if (customStr) return customStr
    return '{}'
  }

  const header = typeName ? `${typeName} {` : '{'
  const inner = entries
    .map(([key, val]) => `${indent(level + 1)}${key}: ${formatStructured(val, seen, level + 1)}`)
    .join(',\n')
  return `${header}\n${inner}\n${indent(level)}}`
}

function formatRuntimeInstance(instance: RuntimeInstance, seen: Seen, level: number): string {
  const entries = Object.entries(instance.fields)
  if (entries.length === 0) return `Instance of ${instance.typeName}`

  const inner = entries
    .map(([key, val]) => `${indent(level + 1)}${key}: ${formatStructured(val, seen, level + 1)}`)
    .join(',\n')
  return `${instance.typeName} {\n${inner}\n${indent(level)}}`
}

function formatStructured(value: unknown, seen: Seen, level: number): string {
  if (value === UNREADABLE) return '[unreadable]'

  if (isPlainPrimitive(value)) return formatPrimitiveQuoted(value)

  if (typeof value === 'function') {
    const fnName = (value as { name?: string }).name
    return `[Function: ${fnName && fnName.length > 0 ? fnName : 'anonymous'}]`
  }

  if (level >= MAX_DEPTH) return '[Max depth reached]'

  const objectValue = value as object
  if (seen.has(objectValue)) return '[Circular]'
  seen.add(objectValue)

  if (value instanceof RuntimeInstance) return formatRuntimeInstance(value, seen, level)

  if (Array.isArray(objectValue)) return formatArray(objectValue, seen, level)
  return formatObject(objectValue, seen, level)
}

export function formatExecutionResult(value: unknown): string {
  if (value === undefined) return 'undefined'
  if (value === null) return 'null'

  if (isPlainPrimitive(value)) {
    if (typeof value === 'string') return value
    if (typeof value === 'bigint') return `${value.toString()}n`
    if (typeof value === 'symbol') return value.toString()
    return String(value)
  }

  if (typeof value === 'function') {
    const fnName = (value as { name?: string }).name
    return `[Function: ${fnName && fnName.length > 0 ? fnName : 'anonymous'}]`
  }

  return formatStructured(value, new WeakSet(), 0)
}
